// Hooks that consume API endpoints
import { useInfiniteQuery, useQuery, useQueryClient, useMutation } from '@tanstack/react-query';

import { DAYS, UserRole } from '../constants';
import { getBanks } from '../services/banks';

// Medical Staff

import { centerEquipmentService } from '../services/centerEquipmentService';
import { getClient, getClients, getSubsidiaries } from '../services/clients';
import { fetchConvenioList } from '../services/convenio';
import { doctorService } from '../services/doctorService';
import { getCurrentDollarRate } from '../services/dollar';
import { getExternalProviderByCase } from '../services/externalProvider';
import { getImagenmedCosts } from '../services/imagenmedCosts';
import { getInsurance } from '../services/insurance';
import { letterOfGuaranteeService } from '../services/letterOfGuaranteeService';
import { getStateLocations } from '../services/locations';
import {
  getCaseById,
  getCases,
  getCasesSubscribe,
  getCasesCount,
  getMedicalCaseStatusHistory,
  getStaleMedicalCases,
  getPendingRequests,
  updateCaseFinancialAgreement,
} from '../services/medicalCases';
import { getConsultDoctors, getCitaMedicaBookings } from '../services/medicalStaff';
import { fetchStudyOriginalData } from '../services/medicalStudy';
import { reimbursementService } from '../services/reimbursementService';
import {
  getDiagnosisList,
  getRequirementMedicalCenters,
  getRequirements,
  getRequirementTypes,
} from '../services/requirements';

// Medical Staff

// @hector

// @hector Duplicate scale

/**
 * Hook to get external provider for a medical case
 * @param {number | string} caseId - ID of the medical case
 */
export function useExternalProvider(caseId) {
  return useQuery({
    queryKey: ['externalProvider', caseId],
    queryFn: () => getExternalProviderByCase(caseId),
    enabled: !!caseId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to get the current doctor's profile.
 * Returns null if the user is not a doctor (403/404).
 */
export function useGetMyProfile() {
  return useQuery({
    queryKey: ['myProfile'],
    queryFn: () => doctorService.getMyProfile(),
    select: data => data,
    staleTime: 1000 * 60 * 15,
  });
}

/**
 *
 * Hook get version of the API
 */
export function useVersion() {
  return useQuery({
    queryKey: ['version'],
    queryFn: () => fetchDashboardVersion(),
    // process.env.REACT_APP_AUTH_URL
    // queryFn: () => fetch(`${import.meta.env.VITE_API_URL}version/`).then((res) => res.json()),
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Hook to get requirement types
 */
export function useRequirementTypes() {
  return useQuery({
    queryKey: ['requirementTypes'],
    queryFn: () => getRequirementTypes(),
    staleTime: 1000 * 60 * 60,
  });
}

/**
 * Hook to get all requirements, or filtered by type
 * @param {number | undefined} requirementType
 * @param {import("@tanstack/react-query").UseQueryOptions} options
 */
export function useRequirements(requirementType, options = {}, isActive = true) {
  return useQuery({
    queryKey: ['requirements', requirementType, { isActive }],
    queryFn: () => getRequirements(requirementType, [], null, null, isActive),
    ...options,
  });
}

/**
 * Hook to get all requirements, or filtered by type and companies
 * @param {number | undefined} requirementType
 * @param {number[]} companies
 */
export function useRequirementsCompanies(requirementType, companies = [], state = null) {
  return useQuery({
    queryKey: ['requirements', requirementType, ...companies, state],
    queryFn: () => getRequirements(requirementType, companies, state),
    enabled: !!companies.length,
  });
}

/**
 * Hook para obtener los requerimientos ofrecidos por un centro médico
 * @param {number} medicalCenter - ID of the medical center
 */
export function useRequirementsByMedicalCenter(medicalCenter, options = {}) {
  return useQuery({
    queryKey: ['requirements', { medicalCenter }],
    queryFn: () => getRequirements(undefined, [], null, medicalCenter),
    enabled: !!medicalCenter,
    ...options,
  });
}

export function useImagenmedCosts(medicalCenterId, options = {}) {
  return useQuery({
    queryKey: ['imagenmedCosts', medicalCenterId],
    queryFn: () => getImagenmedCosts(medicalCenterId),
    enabled: !!medicalCenterId,
    ...options,
  });
}

/**
 * Hook to get all medical cases
 * @param {Object} params
 * @param {number} params.page
 * @param {number} params.pageSize
 * @param {object} params.filters
 * @param {import("@tanstack/react-query").UseQueryOptions} [params.options={}]
 */
export function useMedicalCases({ page, pageSize, filters, options = {} }) {
  return useQuery({
    queryKey: ['medicalCases', page, pageSize, filters],
    queryFn: () => getCases(page, pageSize, filters),
    select: data => {
      return {
        count: data.count,
        results: data.results,
      };
    },
    ...options,
  });
}

/**
 * Hook to get count medical cases
 */
export function useMedicalCasesCount() {
  return useQuery({
    queryKey: ['medicalCasesCount'],
    queryFn: () => getCasesCount(),
    select: data => data,
  });
}

/**
 * Hook to get all medical cases
 */
export function useMedicalCasesSubscribe({ page, pageSize, filters }) {
  return useQuery({
    queryKey: ['medicalCasesSubscribe', page, pageSize, filters],
    queryFn: () => getCasesSubscribe(page, pageSize, filters),
    select: data => {
      return {
        count: data?.count,
        results: data?.results?.map(medicalCase => {
          // Get unique requirement types
          const requirementTypesSet = new Set();
          medicalCase.requirements.forEach(req => requirementTypesSet.add(req.type_requirement));
          // Get all requirement names
          const requirementNameArray = [];
          medicalCase.requirements.forEach(req => requirementNameArray.push(req.requirement_name));
          return {
            ...medicalCase,
            requirement_type_names: Array.from(requirementTypesSet).join(', '),
            requirement_names: requirementNameArray.join(', '),
          };
        }),
      };
    },
  });
}

export function useMedicalCasesSubscribeAll({ page, pageSize, filters, enabled }) {
  return useQuery({
    queryKey: ['medicalCaseSubscribeAll', page, pageSize, filters],
    queryFn: () => getCasesSubscribe(page, pageSize, filters, true),
    enabled: enabled,
    select: data => {
      data.results = data.results.map((medicalCase, index) => ({
        ...medicalCase,
        index: index + 1,
      }));

      return data;
    },
  });
}

/**
 * Hook to get all medical cases
 */
export function useReportMedicalCase({ page, pageSize, filters }) {
  return useQuery({
    queryKey: ['medicalCasesReport', page, pageSize, filters],
    queryFn: () => getReportMedicalCases(page, pageSize, filters),
    select: data => {
      return {
        count: data.count,
        results: data.results.map(medicalCase => {
          return {
            ...medicalCase,
          };
        }),
      };
    },
  });
}

export function useReportMedicalCaseAll({ page, pageSize, filters, enabled }) {
  return useQuery({
    queryKey: ['medicalCasesReportAll', page, pageSize, filters],
    queryFn: () => getReportMedicalCases(page, pageSize, filters, true),
    enabled: enabled,
    select: data => {
      data.results = data.results.map((medicalCase, index) => ({
        ...medicalCase,
        index: index + 1,
      }));

      return data;
    },
  });
}

/**
 * Hook to get single medical case
 * @param {number | string} caseId
 */
export function useSingleMedicalCase(caseId) {
  return useQuery({
    queryKey: ['medicalCase', Number(caseId)],
    queryFn: () => getCaseById(Number(caseId)),
    select: data => ({
      ...data,
      requirements: data.requirements.map(req => ({
        done: false,
        ...req,
      })),
    }),
  });
}

/**
 *
 * hook to save amount of financial agreement
 *
 */
export function useUpdateFinancialAgreement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCaseFinancialAgreement,
    onSuccess: (result, variables) => {
      if (result.type === 'success') {
        // Refrescar caché del caso específico y listas
        queryClient.invalidateQueries({ queryKey: ['medicalCase', Number(variables.caseId)] });
        queryClient.invalidateQueries({ queryKey: ['medicalCases'] });
        queryClient.invalidateQueries({ queryKey: ['medicalCasesSubscribe'] });
      }
    },
  });
}

/**
 * Hook to get stale medical cases
 * @author gabrielm
 */
export function useStaleMedicalCases({ page, pageSize, filters }) {
  return useQuery({
    queryKey: ['medicalCases', 'stale', page, pageSize, filters],
    queryFn: () => getStaleMedicalCases(page, pageSize, filters),
    select: data => {
      return {
        count: data.count,
        results: data.results.map(medicalCase => {
          // Get unique requirement types
          const requirementTypesSet = new Set();
          medicalCase.requirements.forEach(req => requirementTypesSet.add(req.type_requirement));
          // Get all requirement names
          const requirementNameArray = [];
          medicalCase.requirements.forEach(req => requirementNameArray.push(req.requirement_name));
          return {
            ...medicalCase,
            requirement_type_names: Array.from(requirementTypesSet).join(', '),
            requirement_names: requirementNameArray.join(', '),
          };
        }),
      };
    },
  });
}

/**
 * Hook to get medical centers for a requirement
 * @param {number | undefined} requirement
 */
export function useRequirementMedicalCenters(requirement) {
  return useQuery({
    queryKey: ['reqMedicalCenters', requirement],
    queryFn: () => getRequirementMedicalCenters(requirement),
  });
}

/**
 * Hook to get all available banks
 */
export function useBanks() {
  return useQuery({
    queryKey: ['banks'],
    queryFn: () => getBanks(),
    staleTime: Infinity,
  });
}

/**
 * Hook to get list of available diagnosis
 */
export function useDiagnosisList() {
  return useQuery({
    queryKey: ['diagnosisList'],
    queryFn: () => getDiagnosisList(),
    staleTime: Infinity,
    select: data =>
      data.map(diagnosis => ({
        id: diagnosis.id,
        name: `${diagnosis.code} - ${diagnosis.name}`,
      })),
  });
}

/**
 * Hook to get list of clients
 * @param {boolean | undefined} scaleNull
 */
export function useClients(scaleNull, fundType) {
  return useQuery({
    // Los parámetros van en la key para que cada combinación de filtros
    // tenga su propia caché (antes `useClients()` y `useClients(false)`
    // compartían resultados). `invalidateQueries(['clients'])` sigue
    // invalidando todas las variantes por coincidencia de prefijo.
    queryKey: ['clients', scaleNull ?? null, fundType ?? null],
    queryFn: () => getClients(scaleNull, fundType),
  });
}

/**
 * Hook to get client by ID
 * @author gabrielm
 * @param {number?} clientId
 */
export function useClient(clientId) {
  return useQuery({
    queryKey: ['client', clientId],
    queryFn: () => getClient(clientId),
    enabled: !!clientId,
  });
}

/**
 * Hook to get list of subsidiaries
 */
export function useSubsidiaries() {
  return useQuery({
    queryKey: ['subsidiaries'],
    queryFn: () => getSubsidiaries(),
  });
}

export function useCachedRequirementsByClientAndCenter({ clientId, centerId, centerName }) {
  return useQuery({
    queryKey: ['cachedRequirementsByClientAndCenter', clientId, centerId, centerName],
    queryFn: () =>
      getCachedRequirementsByClientAndCenter({
        clientId: Number(clientId),
        centerId: Number(centerId),
        centerName,
      }),
    enabled: clientId !== null && centerId !== null,
    cacheTime: 0, // Disable caching to always fetch fresh data
  });
}

export function useClientList() {
  return useQuery({
    queryKey: ['clientList'],
    queryFn: () => getClientList(),
    select: data => {
      return data?.data.map(client => ({
        id: client.id,
        name: client.name,
      }));
    },
    // keep client list cached for a reasonable time and avoid refetch on mount
    staleTime: 1000 * 60 * 30, // 30 minutes
    cacheTime: 1000 * 60 * 60, // 1 hour
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
}

/**
 * Hook to get the current dollar rate
 *
 */
export function useCurrentDollarRate() {
  return useQuery({
    queryKey: ['dollarRate'],
    queryFn: () => getCurrentDollarRate(),
    // select: (data) => data.oficial.value,
    // staleTime: 1000 * 60 * 60,
  });
}

export function useGetInsurance() {
  const role = localStorage.getItem('role');
  return useQuery({
    queryKey: ['insurance'],
    queryFn: () => getInsurance(),
    staleTime: Infinity,
    enabled: role !== UserRole.MEDICAL_CENTER,
  });
}

export function useUsersList() {
  return useQuery({
    queryKey: ['users'],
    queryFn: () => getusersList(),
    staleTime: Infinity,
  });
}

export function useUserData(userId) {
  return useQuery({
    queryKey: ['users', userId],
    queryFn: () => getUser(userId),
  });
}

/**
 * Obtener permisos
 * @author gabrielm
 * @param {import("@tanstack/react-query").UseQueryOptions} options
 */
export function usePermissions(options = {}) {
  const token = localStorage.getItem('token');
  return useQuery({
    queryKey: ['permissions', token],
    queryFn: getPermissions,
    staleTime: 1000 * 60 * 10,
    enabled: !!token,
    ...options,
  });
}

export function useUsersClientList(userID) {
  return useQuery({
    queryKey: ['usersClient', userID],
    queryFn: () => getUsersClientList(userID),
    staleTime: Infinity,
    enabled: !!userID,
  });
}

export function useUserMedicalCenters() {
  return useQuery({
    queryKey: ['usersClient'],
    queryFn: () => centerEquipmentService.getUserMedicalCenters(),
    staleTime: 1000 * 60 * 5,
  });
}

export function useStateLocations() {
  return useQuery({
    queryKey: ['stateLocations'],
    queryFn: () => getStateLocations(),
  });
}

/**
 * `useQuery` hook to get medical case status history
 * @author gabrielm
 * @param {number} caseId
 * @param {import("@tanstack/react-query").UseQueryOptions} options
 */
export function useMedicalCaseStatusHistory(caseId, options = {}) {
  return useQuery({
    queryKey: ['medicalCaseStatusHistory', caseId],
    queryFn: () => getMedicalCaseStatusHistory(caseId),
    ...options,
  });
}

/**
 * Hook para obtener el historial / trazabilidad de una Solicitud de Carta Aval.
 * Combina cambios en la solicitud + documentos asociados, ordenados
 * descendente por fecha.
 *
 * @param {number} cartaId
 * @param {import("@tanstack/react-query").UseQueryOptions} options
 */
export function useLetterOfGuaranteeHistory(cartaId, options = {}) {
  return useQuery({
    queryKey: ['letter-of-guarantee', 'history', cartaId],
    queryFn: () => letterOfGuaranteeService.history(cartaId),
    ...options,
  });
}

/**
 * Hook para obtener las notas humanas (bitácora) de una Solicitud
 * de Carta Aval, en orden descendente por fecha.
 *
 * @param {number} cartaId
 * @param {import("@tanstack/react-query").UseQueryOptions} options
 */
export function useLetterOfGuaranteeNotes(cartaId, options = {}) {
  return useQuery({
    queryKey: ['letter-of-guarantee', 'notes', cartaId],
    queryFn: () => letterOfGuaranteeService.listNotes(cartaId),
    ...options,
  });
}

/**
 * Hook para crear una nota nueva en la bitácora. Invalida el cache
 * de notas del mismo cartaId al tener éxito.
 */
export function useAddLetterOfGuaranteeNote(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ cartaId, body }) => letterOfGuaranteeService.addNote(cartaId, body),
    onSuccess: (_, { cartaId }) => {
      qc.invalidateQueries({ queryKey: ['letter-of-guarantee', 'notes', cartaId] });
    },
    ...options,
  });
}

/**
 * Hook to get doctors grouped by their category or specialty
 * @param {Array<number>} specialties - List of specialty IDs to filter doctors
 */
export function useConsultDoctorsQuery(specialties) {
  return useQuery({
    queryKey: ['consultDoctors', specialties],
    queryFn: () => getConsultDoctors(specialties),
    enabled: !!specialties?.length, // Only fetch if specialties are provided
    select: response => {
      // Remove duplicate doctors based on their ID
      const uniqueDoctors = new Map();
      response.data.forEach(doctor => {
        if (!uniqueDoctors.has(doctor.id)) {
          uniqueDoctors.set(doctor.id, doctor);
        }
      });

      // Group doctors by their specialties
      const groupedDoctors = {};
      Array.from(uniqueDoctors.values()).forEach(doctor => {
        doctor.specialities.forEach(specialty => {
          if (!groupedDoctors[specialty.id]) {
            groupedDoctors[specialty.id] = {
              specialtyName: specialty.name,
              doctors: [],
            };
          }
          groupedDoctors[specialty.id].doctors.push(doctor);
        });
      });

      return groupedDoctors;
    },
  });
}

/**
 * React Query hook to fetch bookings for a medical case.
 * @param {number} medicalCaseId - The ID of the medical case.
 * @returns {object} - React Query result object.
 */
export const useCitaMedicaBookings = medicalCaseId => {
  return useQuery({
    queryKey: ['bookings', medicalCaseId],
    queryFn: () => getCitaMedicaBookings(medicalCaseId),
    enabled: !!medicalCaseId, // Only fetch if medicalCaseId is provided
    select: response => {
      return response.data;
    },
  });
};

/**
 * Hook to get profile picture
 * @param {object} params
 * @param {string?} params.token
 */
export function useProfilePicture({ token }) {
  return useQuery({
    queryKey: ['profilePicture', token],
    queryFn: () => getProfilePicture(),
    enabled: !!token,
  });
}

/**
 * Hook to fetch pending requests
 * @author gabrielm
 * @param {object} params
 */
export function usePendingRequests({ page, pageSize, filters }) {
  return useQuery({
    queryKey: ['medicalCases', page, pageSize, filters],
    queryFn: () => getPendingRequests(page, pageSize, filters),
    select: data => ({
      count: data.count,
      results: data.results,
    }),
  });
}

/**
 * Hook to fetch dependency insured by CI type and number
 * @author gabrielm
 * @param {string} ciType
 * @param {string} ciNumber
 * @param {string} subsidiaryId
 * @param {import("@tanstack/react-query").UseQueryOptions} options
 */
export function useDependencyInsured(ciType, ciNumber, subsidiaryId, options = {}) {
  return useQuery({
    queryKey: ['dependencyInsured', ciType, ciNumber, subsidiaryId],
    queryFn: () => getDependencyInsured(ciType, ciNumber, subsidiaryId),
    enabled: !!ciType && !!ciNumber && !!subsidiaryId,
    ...options,
  });
}

/**
 * Hook to check if a user can add requirements to a case in call center
 * @author gabrielm
 * @param {string | undefined} userId
 * @param {string} clientId
 */
export function useCanAddRequirementsCallCenter(userId, clientId, options = {}) {
  return useQuery({
    queryKey: ['canAddRequirementsCallCenter', userId, clientId],
    queryFn: () => canAddRequirementsToCaseCallCenter(userId, clientId),
    retry: 2,
    ...options,
  });
}

/**
 * Hook to fetch convenio list
 */
export function useConvenioListQuery() {
  return useQuery({
    queryKey: ['convenioList'],
    queryFn: fetchConvenioList,
    staleTime: 1000 * 60 * 10,
  });
}

/**
 * Hook to get original patient/study data directly from MedicalStudy
 * Uses existing endpoint: /insurance/medical-study/{id}/
 * @param {number|string|null} studyId - Study ID or token
 * @param {import("@tanstack/react-query").UseQueryOptions} options
 */
export function useStudyOriginalData(studyId, options = {}) {
  return useQuery({
    queryKey: ['studyOriginalData', studyId],
    queryFn: () => fetchStudyOriginalData(studyId),
    enabled: !!studyId,
    staleTime: 1000 * 60 * 5, // 5 minutos: los datos DICOM no cambian
    ...options,
  });
}

import { getReportMedicalCases } from '../services/reports';
import { getClientList } from '../services/requirementCached';
import { getCachedRequirementsByClientAndCenter } from '../services/requirementMedicalCenterCache';
import { getDependencyInsured } from '../services/rms';
import { fetchDashboardVersion } from '../services/version';

import {
  getusersList,
  getUsersClientList,
  getProfilePicture,
  canAddRequirementsToCaseCallCenter,
  getUser,
  getPermissions,
} from '@/modules/access/api/users';

export function useLetterOfGuaranteeList({ filters = {}, options = {} } = {}) {
  return useQuery({
    queryKey: ['letter-of-guarantee', 'list', filters],
    queryFn: () => letterOfGuaranteeService.list(filters),
    ...options,
  });
}

export function useLetterOfGuarantee(id, options = {}) {
  return useQuery({
    queryKey: ['letter-of-guarantee', id],
    queryFn: () => letterOfGuaranteeService.retrieve(id),
    enabled: !!id,
    ...options,
  });
}

export function useCreateLetterOfGuarantee(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: formData => letterOfGuaranteeService.create(formData),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['letter-of-guarantee'] }),
    ...options,
  });
}

export function useUpdateLetterOfGuarantee(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, formData }) => letterOfGuaranteeService.update(id, formData),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['letter-of-guarantee'] });
      qc.invalidateQueries({ queryKey: ['letter-of-guarantee', id] });
    },
    ...options,
  });
}

export function useApproveLetterOfGuarantee(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      observation,
      approvalFile,
      approvalKey,
      approvalFileName,
      approvedAmount,
    }) =>
      letterOfGuaranteeService.approve(id, {
        observation,
        approvalFile,
        approvalKey,
        approvalFileName,
        approvedAmount,
      }),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['letter-of-guarantee'] });
      qc.invalidateQueries({ queryKey: ['letter-of-guarantee', id] });
    },
    ...options,
  });
}

export function useRejectLetterOfGuarantee(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, observation, approvalFile, approvalKey, approvalFileName }) =>
      letterOfGuaranteeService.reject(id, {
        observation,
        approvalFile,
        approvalKey,
        approvalFileName,
      }),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['letter-of-guarantee'] });
      qc.invalidateQueries({ queryKey: ['letter-of-guarantee', id] });
    },
    ...options,
  });
}

/**
 * Agregar un documento (soporte) a una solicitud existente.
 * Si estaba en pendiente_documento, el backend la regresa a recibido
 * y el frontend refleja el cambio vía invalidación de queries.
 *
 * Acepta dos formas (mismas que el servicio):
 *   - { id, file, category }            → legacy multipart
 *   - { id, opts: { key, file_name, file_type, category } } → S3
 */
export function useAddLetterDocument(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file, category, opts }) =>
      opts
        ? letterOfGuaranteeService.addDocument(id, opts)
        : letterOfGuaranteeService.addDocument(id, file, category),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['letter-of-guarantee'] });
      qc.invalidateQueries({ queryKey: ['letter-of-guarantee', id] });
    },
    ...options,
  });
}

/**
 * Cambiar el status a uno INTERNO (asesoria_medica, en_negociacion,
 * pendiente_documento). Solo MASTER o ADMIN. Desde cualquier estado
 * INTERMEDIO (no terminal).
 *
 * Payload: { id, status, observation, missingCategories? }
 */
export function useSetInternalStatusLetter(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, observation, missingCategories }) =>
      letterOfGuaranteeService.setInternalStatus(id, {
        status,
        observation,
        missingCategories,
      }),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['letter-of-guarantee'] });
      qc.invalidateQueries({ queryKey: ['letter-of-guarantee', id] });
    },
    ...options,
  });
}

/**
 * Marcar la solicitud como entregada (status === entregado).
 * Roles permitidos: MASTER, CALL_CENTER, MEDICAL_CENTER.
 * Solo si status === aprobado.
 */
export function useMarkDeliveredLetter(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }) => letterOfGuaranteeService.markDelivered(id),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['letter-of-guarantee'] });
      qc.invalidateQueries({ queryKey: ['letter-of-guarantee', id] });
    },
    ...options,
  });
}

// === Reimbursement ===

export function useReimbursementList({ filters = {}, options = {} } = {}) {
  return useQuery({
    queryKey: ['reimbursement', 'list', filters],
    queryFn: () => reimbursementService.list(filters),
    ...options,
  });
}

export function useReimbursement(id, options = {}) {
  return useQuery({
    queryKey: ['reimbursement', id],
    queryFn: () => reimbursementService.retrieve(id),
    enabled: !!id,
    ...options,
  });
}

export function useCreateReimbursement(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: formData => reimbursementService.create(formData),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reimbursement'] }),
    ...options,
  });
}

export function useUpdateReimbursement(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, formData }) => reimbursementService.update(id, formData),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['reimbursement'] });
      qc.invalidateQueries({ queryKey: ['reimbursement', id] });
    },
    ...options,
  });
}

export function useApproveReimbursement(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      observation,
      approvalFile,
      approvalKey,
      approvalFileName,
      approvedAmount,
    }) =>
      reimbursementService.approve(id, {
        observation,
        approvalFile,
        approvalKey,
        approvalFileName,
        approvedAmount,
      }),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['reimbursement'] });
      qc.invalidateQueries({ queryKey: ['reimbursement', id] });
    },
    ...options,
  });
}

export function useRejectReimbursement(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, observation, approvalFile, approvalKey, approvalFileName }) =>
      reimbursementService.reject(id, {
        observation,
        approvalFile,
        approvalKey,
        approvalFileName,
      }),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['reimbursement'] });
      qc.invalidateQueries({ queryKey: ['reimbursement', id] });
    },
    ...options,
  });
}

export function useAddReimbursementDocument(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file, category, opts }) =>
      opts
        ? reimbursementService.addDocument(id, opts)
        : reimbursementService.addDocument(id, file, category),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['reimbursement'] });
      qc.invalidateQueries({ queryKey: ['reimbursement', id] });
    },
    ...options,
  });
}

export function useSetInternalStatusReimbursement(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, observation, missingCategories }) =>
      reimbursementService.setInternalStatus(id, {
        status,
        observation,
        missingCategories,
      }),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['reimbursement'] });
      qc.invalidateQueries({ queryKey: ['reimbursement', id] });
    },
    ...options,
  });
}

export function useMarkDeliveredReimbursement(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }) => reimbursementService.markDelivered(id),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['reimbursement'] });
      qc.invalidateQueries({ queryKey: ['reimbursement', id] });
    },
    ...options,
  });
}

export function useReimbursementHistory(reembolsoId, options = {}) {
  return useQuery({
    queryKey: ['reimbursement', 'history', reembolsoId],
    queryFn: () => reimbursementService.history(reembolsoId),
    ...options,
  });
}

export function useReimbursementNotes(reembolsoId, options = {}) {
  return useQuery({
    queryKey: ['reimbursement', 'notes', reembolsoId],
    queryFn: () => reimbursementService.listNotes(reembolsoId),
    ...options,
  });
}

export function useAddReimbursementNote(options = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ reembolsoId, body }) => reimbursementService.addNote(reembolsoId, body),
    onSuccess: (_, { reembolsoId }) => {
      qc.invalidateQueries({
        queryKey: ['reimbursement', 'notes', reembolsoId],
      });
    },
    ...options,
  });
}
